import { useMemo, useState } from 'react';
import { useAI } from '../../context/AIContext';
import { useApp } from '../../context/AppContext';

const QUICK_QUESTIONS = [
  'What are my top 3 actions right now?',
  'How can I reduce my diet emissions?',
  'Give me a one-week starter plan.',
];

export function AIAssistant() {
  const { messages, isLoading, aiError, recommendations, askCoach, refreshRecommendations, clearChat } = useAI();
  const { items, footprint, region } = useApp();
  const [input, setInput] = useState('');

  const recommendationRows = useMemo(() => {
    return recommendations
      .map((rec) => {
        const action = items.find((item) => item.id === rec.actionId);
        if (!action) return null;
        return { ...action, reason: rec.reason, priority: rec.priority };
      })
      .filter(Boolean)
      .sort((a, b) => a.priority - b.priority);
  }, [recommendations, items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const message = input;
    setInput('');
    await askCoach(message);
  };

  return (
    <div style={{ animation: 'slideIn 0.3s ease' }}>
      <h2 style={{ fontSize: '1.6rem', fontWeight: 'normal', color: '#f0fdf4', marginBottom: '0.25rem' }}>
        AI <em style={{ color: '#c4b5fd' }}>Carbon Coach</em>
      </h2>
      <p style={{ color: '#6b7280', fontSize: '0.83rem', marginBottom: '1rem' }}>
        Ask anything about lowering your footprint. Current profile: {footprint.toFixed(1)}t in {region.toUpperCase()}.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
        {QUICK_QUESTIONS.map((question) => (
          <button
            key={question}
            onClick={() => askCoach(question)}
            style={{
              border: '1px solid rgba(168,85,247,0.25)',
              background: 'rgba(168,85,247,0.08)',
              color: '#d8b4fe',
              borderRadius: '999px',
              padding: '0.35rem 0.7rem',
              fontSize: '0.72rem',
            }}
          >
            {question}
          </button>
        ))}
      </div>

      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          padding: '0.8rem',
          minHeight: '260px',
          maxHeight: '380px',
          overflowY: 'auto',
          marginBottom: '0.8rem',
        }}
      >
        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            style={{
              marginBottom: '0.6rem',
              display: 'flex',
              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: '85%',
                padding: '0.6rem 0.75rem',
                borderRadius: '10px',
                fontSize: '0.82rem',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                background: message.role === 'user' ? 'rgba(34,197,94,0.16)' : 'rgba(168,85,247,0.12)',
                border: message.role === 'user' ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(168,85,247,0.25)',
                color: message.role === 'user' ? '#bbf7d0' : '#ede9fe',
              }}
            >
              {message.content}
            </div>
          </div>
        ))}
        {isLoading && <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>AI is thinking...</div>}
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.55rem' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask your climate question..."
          style={{
            flex: 1,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: '#e8f5e9',
            borderRadius: '10px',
            padding: '0.72rem 0.85rem',
            fontSize: '0.85rem',
          }}
        />
        <button
          type="submit"
          disabled={isLoading}
          style={{
            background: 'rgba(34,197,94,0.2)',
            border: '1px solid rgba(34,197,94,0.34)',
            color: '#86efac',
            borderRadius: '10px',
            padding: '0 0.95rem',
            fontSize: '0.8rem',
          }}
        >
          Send
        </button>
      </form>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem', marginBottom: '1rem' }}>
        <button
          onClick={refreshRecommendations}
          style={{
            background: 'rgba(59,130,246,0.16)',
            border: '1px solid rgba(59,130,246,0.28)',
            color: '#93c5fd',
            borderRadius: '8px',
            padding: '0.42rem 0.7rem',
            fontSize: '0.75rem',
          }}
        >
          Refresh Recommendations
        </button>
        <button
          onClick={clearChat}
          style={{
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: '#9ca3af',
            borderRadius: '8px',
            padding: '0.42rem 0.7rem',
            fontSize: '0.75rem',
          }}
        >
          Clear Chat
        </button>
      </div>

      {aiError && (
        <div style={{ color: '#fda4af', fontSize: '0.75rem', marginBottom: '0.7rem' }}>Error: {aiError}</div>
      )}

      {recommendationRows.length > 0 && (
        <div
          style={{
            background: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.18)',
            borderRadius: '12px',
            padding: '0.9rem',
          }}
        >
          <div style={{ color: '#86efac', fontSize: '0.73rem', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Top AI Recommendations
          </div>
          {recommendationRows.map((row) => (
            <div key={row.id} style={{ marginBottom: '0.55rem' }}>
              <div style={{ color: '#dcfce7', fontSize: '0.82rem' }}>
                {row.emoji} {row.title}
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.72rem', lineHeight: 1.45 }}>{row.reason}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
